import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { products } from '../data/mock';
import { Card, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CheckCircle2,
  ArrowRight,
  MessageSquare,
  Leaf,
  Shield,
  Globe,
  Fuel,
  Wallet,
  MapPin,
  Sparkles,
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { PhoneSlideshow } from '../components/fueliq/PhoneShowcase';

export default function ProductsPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Products & Ventures"
        path="/products"
        description="Quality products, global trade and technology-driven ventures — including FuelIQ, our smart fuel-price comparison platform — connecting opportunities across Australia and global markets."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900" data-testid="products-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium mb-6">
                Our Products & Ventures
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Products &
                <span className="block text-teal-400">Ventures</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Quality products, global trade and technology-driven ventures,
                connecting opportunities across Australia and global markets.
              </p>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex items-center gap-2 text-slate-600">
                <Shield className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium">Quality Assured</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Globe className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium">Import & Export Ready</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Leaf className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium">Sustainable Sourcing</span>
              </div>
            </div>
          </div>
        </section>

        {/* Global Trade & Sourcing */}
        <section className="py-20 bg-white" data-testid="global-trade-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold tracking-wide uppercase mb-4">
                Global Trade & Sourcing
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900">
                Trade Products
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {products.map((product) => (
                <Card
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="group overflow-hidden border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300"
                >
                  <div className="grid md:grid-cols-2">
                    {/* Image */}
                    <div className="relative h-64 md:h-full overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.category}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-navy-900/30 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-teal-600 text-white">
                          {product.origin}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <CardTitle className="text-xl text-navy-900 mb-3">
                        {product.category}
                      </CardTitle>
                      <p className="text-slate-600 mb-4">{product.description}</p>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-navy-900 mb-2">
                          Available Products:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {product.items.map((item) => (
                            <span
                              key={item}
                              className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-navy-900 mb-2">
                          Compliance:
                        </h4>
                        <div className="space-y-1">
                          {product.compliance.map((cert) => (
                            <div
                              key={cert}
                              className="flex items-center gap-2 text-sm text-teal-600"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              <span>{cert}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Link to="/contact">
                        <Button className="w-full bg-navy-900 hover:bg-navy-800 text-white">
                          Enquire Now
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Technology & Digital Ventures */}
        <section
          className="py-20 bg-navy-900"
          data-testid="tech-ventures-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-semibold tracking-wide uppercase mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                Technology & Digital Ventures
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Building Digital Products
              </h2>
            </div>

            {/* FuelIQ Feature Card */}
            <div
              data-testid="fueliq-card"
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-navy-800 to-navy-900"
            >
              <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />

              <div className="relative grid lg:grid-cols-2 gap-10 p-8 md:p-14 items-center">
                {/* Copy */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl overflow-hidden ring-1 ring-white/10 bg-[#0a1f33]">
                      <img
                        src="/images/fueliq-logo.png"
                        alt="FuelIQ logo"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-white leading-none">
                        FuelIQ
                      </h3>
                      <p className="text-amber-400 font-medium mt-1">
                        Find. Save. Go Further.
                      </p>
                    </div>
                  </div>

                  <p className="text-lg text-slate-300 mb-8 max-w-md">
                    Smart fuel-price comparison & savings platform for Australian
                    drivers.
                  </p>

                  <div className="flex flex-wrap gap-3 mb-10">
                    <div className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-2.5 ring-1 ring-white/10">
                      <Fuel className="h-5 w-5 text-teal-400" />
                      <span className="text-sm font-medium text-white">
                        Live Prices
                      </span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-2.5 ring-1 ring-white/10">
                      <Wallet className="h-5 w-5 text-amber-400" />
                      <span className="text-sm font-medium text-white">
                        Savings
                      </span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-2.5 ring-1 ring-white/10">
                      <MapPin className="h-5 w-5 text-teal-400" />
                      <span className="text-sm font-medium text-white">
                        Nearby
                      </span>
                    </div>
                  </div>

                  <div>
                    <Link to="/fueliq">
                      <Button
                        size="lg"
                        data-testid="explore-fueliq-btn"
                        className="bg-teal-500 hover:bg-teal-400 text-navy-900 font-semibold px-8 rounded-xl"
                      >
                        Explore FuelIQ
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Phone mockup — auto-playing app preview */}
                <div className="flex justify-center lg:justify-end">
                  <PhoneSlideshow testid="fueliq-mockup-slideshow" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Custom Sourcing CTA */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <MessageSquare className="h-16 w-16 text-teal-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-navy-900 mb-4">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Our sourcing team can locate and verify suppliers for specific
              products based on your unique requirements.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 rounded-xl"
              >
                Request Custom Sourcing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
